import React, { useCallback, useMemo } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import Tabs from '../../../../../components/display/Tabs';
import { ActionsPage } from '../../GeneralView/ActionsPage';
import { AccidentsPage } from '../../GeneralView/AccidentsPage';
import { CommunicationsPage } from '../../GeneralView/CommunicationsPage';
import { MultimediaPage } from '../../GeneralView/MultimediaPage';

// Types
import { tabsType } from '../../../../../state/reducers/ui/tabs';
import { tableIds } from '../../../../../components/display/tables/types';

// Styles
import { ContainerStyled } from './styles';

// ═══════════════════════════════════════════════════════════════════
// TAB PATH HELPERS
// ═══════════════════════════════════════════════════════════════════
// Pestañas de módulo del LISTADO de Campañas (`/work-orders/campaign`) —
// espejo EXACTO del patrón de `GeneralViewRoutePage` (listado del módulo RV,
// `src/pages/main/surveillanceRoute/GeneralViewRoute/index.tsx`, fase 3 nav
// corregida, 49900ac3): las 4 páginas globales de entidad (Fase 2) se
// reutilizan con `containerTypes: ['campaign']` (filtro por TIPO de origen —
// "todas las actuaciones/accidentes/comunicaciones/multimedia de campañas",
// NO las de una campaña concreta: para eso se entra en el detalle, cuyo
// ShowOT ya lista sus hijas). A diferencia del RV (solo Actuaciones/
// Multimedia por restricción de modelo), una Campaña admite las 4 entidades,
// así que aquí van las 4.
const TAB_PATHS_BASE = '/work-orders/campaign';

const TAB_PATHS = [
  TAB_PATHS_BASE,
  `${TAB_PATHS_BASE}/actions`,
  `${TAB_PATHS_BASE}/accidents`,
  `${TAB_PATHS_BASE}/communications`,
  `${TAB_PATHS_BASE}/multimedia-list`,
];

const getIndexFromPath = (pathname: string): number => {
  const normalized = pathname.replace(/\/$/, '');
  const idx = TAB_PATHS.indexOf(normalized);
  return idx >= 0 ? idx : 0;
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function GeneralViewCampaignPage(): JSX.Element {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const location = useLocation();

  // URL is the source of truth for the active tab on mount/reload
  const urlIndex = getIndexFromPath(location.pathname);

  const dataTab = useMemo(
    () => [
      { label: t('campaigns.title') },
      { label: t('workOrders.actions') },
      { label: t('workOrders.accidents') },
      { label: t('workOrders.communications') },
      { label: t('workOrders.multimedia') },
    ],
    [t]
  );

  // Navigate when the selected tab changes (via click).
  // On mount/reload this is a no-op because the URL already matches.
  const handleTabChange = useCallback(
    (newIndex: number) => {
      const currentPath = location.pathname.replace(/\/$/, '');
      const targetPath = TAB_PATHS[newIndex] || TAB_PATHS[0];

      if (currentPath !== targetPath) {
        navigate(targetPath);
      }
    },
    [location.pathname, navigate]
  );

  return (
    <ContainerStyled>
      <Tabs
        data={dataTab}
        typeTab={tabsType.CAMPAIGN_LIST_GENERAL}
        defaultIndex={urlIndex}
        setSelectedIndexTab={handleTabChange}
      />
      <Outlet />
    </ContainerStyled>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CONTEXTUAL TAB BINDINGS
// ═══════════════════════════════════════════════════════════════════
// Las 4 páginas globales de entidad (Fase 2) se parametrizan con
// `containerTypes` ESTÁTICO (no hay ningún `:id` en estas 4 rutas — son
// hermanas estáticas del `index` de `/work-orders/campaign`, mismo criterio
// que `RouteActionsTab`/`RouteMultimediaTab` de `GeneralViewRoute/index.tsx`).
const CAMPAIGN_LIST_CONTAINER_TYPES = ['campaign'];

export function CampaignListActionsTab(): JSX.Element {
  return (
    <ActionsPage
      containerTypes={CAMPAIGN_LIST_CONTAINER_TYPES}
      tableId={tableIds.CAMPAIGN_LIST_ACTIONS}
      origin="campaign-list-actions-tab"
      returnTo={`${TAB_PATHS_BASE}/actions`}
    />
  );
}

export function CampaignListAccidentsTab(): JSX.Element {
  return (
    <AccidentsPage
      containerTypes={CAMPAIGN_LIST_CONTAINER_TYPES}
      tableId={tableIds.CAMPAIGN_LIST_ACCIDENTS}
      origin="campaign-list-accidents-tab"
      returnTo={`${TAB_PATHS_BASE}/accidents`}
    />
  );
}

export function CampaignListCommunicationsTab(): JSX.Element {
  return (
    <CommunicationsPage
      containerTypes={CAMPAIGN_LIST_CONTAINER_TYPES}
      tableId={tableIds.CAMPAIGN_LIST_COMMUNICATIONS}
      origin="campaign-list-communications-tab"
      returnTo={`${TAB_PATHS_BASE}/communications`}
    />
  );
}

export function CampaignListMultimediaTab(): JSX.Element {
  return (
    <MultimediaPage
      containerTypes={CAMPAIGN_LIST_CONTAINER_TYPES}
      tableId={tableIds.CAMPAIGN_LIST_MULTIMEDIA}
      origin="campaign-list-multimedia-tab"
      returnTo={`${TAB_PATHS_BASE}/multimedia-list`}
    />
  );
}
